import * as React from 'react';

import { Accordion, Badge } from '@rematter/ui-web';
import { LucideCircleAlert, LucideTriangleAlert } from 'lucide-react';

import type { EvaluatedTierData } from '../../_graphql/queryEvaluateFormulas';
import { useTranslations } from '../../i18n/useTranslations';
import type { ItemPricingRecord, ValidationIssue } from '../../types/pricing';

type Props = {
  itemPricings: ItemPricingRecord[];
  evaluatedPrices: Record<string, EvaluatedTierData>;
  pricingMode?: 'Standard' | 'CRV';
};

const deriveValidationIssues = (
  itemPricings: ItemPricingRecord[],
  evaluatedPrices: Record<string, EvaluatedTierData>,
  pricingMode?: string,
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const isCRV = pricingMode === 'CRV';

  for (const pp of itemPricings) {
    const itemName = pp.item.name;

    for (let i = 0; i < pp.tiers.length; i++) {
      const tier = pp.tiers[i];
      const evaluated = evaluatedPrices[tier.id];

      if (!isCRV) {
        if (
          tier.targetPrice.formula &&
          evaluated &&
          evaluated.targetPrice == null
        ) {
          issues.push({
            severity: 'error',
            itemName,
            tierIndex: i,
            message: `Target price formula "${tier.targetPrice.formula}" could not be evaluated`,
          });
        }

        if (tier.minPrice.formula && evaluated && evaluated.minPrice == null) {
          issues.push({
            severity: 'error',
            itemName,
            tierIndex: i,
            message: `Min price formula "${tier.minPrice.formula}" could not be evaluated`,
          });
        }

        if (tier.maxPrice.formula && evaluated && evaluated.maxPrice == null) {
          issues.push({
            severity: 'error',
            itemName,
            tierIndex: i,
            message: `Max price formula "${tier.maxPrice.formula}" could not be evaluated`,
          });
        }

        if (
          evaluated?.minPrice != null &&
          evaluated?.targetPrice != null &&
          evaluated.minPrice > evaluated.targetPrice
        ) {
          issues.push({
            severity: 'warning',
            itemName,
            tierIndex: i,
            message: `Min price (${evaluated.minPrice}) exceeds target price (${evaluated.targetPrice})`,
          });
        }

        if (
          evaluated?.targetPrice != null &&
          evaluated?.maxPrice != null &&
          evaluated.targetPrice > evaluated.maxPrice
        ) {
          issues.push({
            severity: 'warning',
            itemName,
            tierIndex: i,
            message: `Target price (${evaluated.targetPrice}) exceeds max price (${evaluated.maxPrice})`,
          });
        }
      }
    }
  }

  return issues;
};

export const ValidationErrorsPanel = ({
  itemPricings,
  evaluatedPrices,
  pricingMode,
}: Props) => {
  const t = useTranslations();

  const issues = React.useMemo(
    () => deriveValidationIssues(itemPricings, evaluatedPrices, pricingMode),
    [itemPricings, evaluatedPrices, pricingMode],
  );

  if (issues.length === 0) return null;

  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const warningCount = issues.filter((i) => i.severity === 'warning').length;

  return (
    <Accordion
      variant="ghost"
      size="sm"
      className="@ValidationErrorsPanel my-2 rounded border-neutral/20 bg-neutral/5"
      trigger={
        <div className="flex items-center gap-2 rounded-sm">
          <span className="text-sm font-medium">
            {t.pricing.validation.title}
          </span>
          {errorCount > 0 && (
            <Badge color="failure" size="small">
              {t.pricing.validation.error(errorCount)}
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge color="warning" size="small">
              {t.pricing.validation.warning(warningCount)}
            </Badge>
          )}
        </div>
      }
    >
      <div className="max-h-48 overflow-y-auto">
        <ul className="space-y-1">
          {issues.map((issue, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <span
                className={
                  issue.severity === 'error'
                    ? 'mt-0.5 text-failure'
                    : 'mt-0.5 text-warning'
                }
              >
                {issue.severity === 'error' ? (
                  <LucideCircleAlert size={14} />
                ) : (
                  <LucideTriangleAlert size={14} />
                )}
              </span>
              <span>
                <span className="font-medium">{issue.itemName}</span>
                <span className="text-neutral/60">
                  {' '}
                  ({t.pricing.validation.tier(issue.tierIndex + 1)})
                </span>
                {' — '}
                {issue.message}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Accordion>
  );
};
